import api from './api';
import type { ApiResponse, Hotel } from '@/types';

// =============================================================================
// Hotels (the registry a trip's accommodation block picks from)
// =============================================================================

export interface CreateHotelData {
  name: string;
  city?: string | null;
  address?: string | null;
  phone?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  notes?: string | null;
  is_active?: boolean;
  // ArtDent 260918 — the full rate list; replaces what is stored when sent.
  rates?: { persons: number; price_per_night: number; currency?: string | null }[];
}

export type UpdateHotelData = Partial<CreateHotelData>;

export const getHotels = async (options: { activeOnly?: boolean; search?: string } = {}) => {
  const params = new URLSearchParams();
  if (options.activeOnly) params.append('active_only', 'true');
  if (options.search) params.append('search', options.search);
  const response = await api.get<ApiResponse<Hotel[]>>(`/hotels?${params}`);
  return response.data.data;
};

/**
 * Also the inline "add this one" path from the trip form. The backend returns
 * the existing row (200) when the name already exists, so calling this with a
 * name someone typed before reuses that hotel rather than duplicating it.
 */
export const createHotel = async (data: CreateHotelData) => {
  const response = await api.post<ApiResponse<Hotel>>('/hotels', data);
  return response.data.data;
};

/**
 * The same call for Settings > Hotels, which must TELL the user when the name
 * was already taken: there the 200 means nothing was added and the details
 * just typed were dropped, which the trip form's inline add never cares about.
 */
export const createHotelIfNew = async (data: CreateHotelData) => {
  const response = await api.post<ApiResponse<Hotel>>('/hotels', data);
  return { hotel: response.data.data, created: response.status === 201 };
};

export const updateHotel = async (id: string, data: UpdateHotelData) => {
  const response = await api.put<ApiResponse<Hotel>>(`/hotels/${id}`, data);
  return response.data.data;
};

export const deleteHotel = async (id: string) => {
  await api.delete(`/hotels/${id}`);
};
